import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, Clock, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import CalendarEventDialog from "./CalendarEventDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CalendarPage = () => {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    fetchCalendarData();
  }, [currentDate]);

  const fetchCalendarData = async () => {
    const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

    try {
      // Fetch CRM events
      const eventsResponse = await axios.get(`${API}/calendar/events`, {
        params: {
          start_date: startOfMonth.toISOString(),
          end_date: endOfMonth.toISOString()
        },
        withCredentials: true
      });
      
      // Fetch synced calendar events (Gmail & Outlook)
      const syncedEventsResponse = await axios.get(`${API}/calendar-sync/events`, {
        withCredentials: true
      });
      
      // Combine CRM and synced events
      const allEvents = [
        ...eventsResponse.data,
        ...(syncedEventsResponse.data.events || [])
      ];
      
      setEvents(allEvents);

      // Fetch tasks
      const tasksResponse = await axios.get(`${API}/tasks`, {
        withCredentials: true
      });
      setTasks(tasksResponse.data);
      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching calendar data:", error);
      toast.error("Failed to fetch calendar data");
      setLoading(false);
    }
  };

  const syncCalendars = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/calendar-sync/sync`, {}, {
        withCredentials: true
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchCalendarData();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Calendar sync error:", error);
      toast.error(error.response?.data?.detail || "Failed to sync calendars");
    }
    setSyncing(false);
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days in month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    return events.filter(event => {
      const eventDate = new Date(event.start_time);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const getTasksForDate = (date) => {
    if (!date) return [];
    return tasks.filter(task => {
      if (!task.due_date) return false;
      const taskDate = new Date(task.due_date);
      return taskDate.toDateString() === date.toDateString();
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "bg-red-500";
      case "medium": return "bg-yellow-500";
      case "low": return "bg-green-500";
      default: return "bg-slate-500";
    }
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    });
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const days = getDaysInMonth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-slate-500">Loading calendar...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Calendar</h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">Your events, meetings, and tasks</p>
        </div>
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            onClick={syncCalendars}
            disabled={syncing}
            className="px-3"
          >
            <RefreshCw className={`w-4 h-4 sm:mr-2 ${syncing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{syncing ? 'Syncing...' : 'Sync Calendars'}</span>
          </Button>
          <Button onClick={() => {
            setSelectedEvent(null);
            setSelectedDate(null);
            setShowEventDialog(true);
          }}
          className="px-3">
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">New Event</span>
          </Button>
        </div>
      </div>

      {/* Calendar Controls */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="outline" size="sm" onClick={previousMonth}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <h2 className="text-xl font-semibold min-w-[180px] text-center">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <Button variant="outline" size="sm" onClick={nextMonth}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <Button variant="outline" onClick={goToToday}>
              Today
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            {dayNames.map(day => (
              <div key={day} className="text-center text-sm font-semibold text-slate-600 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-2">
            {days.map((date, index) => {
              const dayEvents = getEventsForDate(date);
              const dayTasks = getTasksForDate(date);
              const hasItems = dayEvents.length > 0 || dayTasks.length > 0;

              return (
                <div
                  key={index}
                  className={`min-h-[120px] border rounded-lg p-2 transition-colors ${
                    date ? "hover:bg-slate-50 cursor-pointer" : "bg-slate-50"
                  } ${isToday(date) ? "border-indigo-500 border-2 bg-indigo-50" : "border-slate-200"}`}
                  onClick={() => {
                    if (date) {
                      setSelectedDate(date);
                      setShowEventDialog(true);
                    }
                  }}
                >
                  {date && (
                    <>
                      <div className={`text-sm font-semibold mb-2 ${
                        isToday(date) ? "text-indigo-700" : "text-slate-700"
                      }`}>
                        {date.getDate()}
                      </div>
                      
                      {hasItems && (
                        <div className="space-y-1">
                          {/* Events */}
                          {dayEvents.slice(0, 2).map(event => {
                            const bgColor = event.provider === "gmail" ? "bg-red-100 text-red-700 hover:bg-red-200" : 
                                          event.provider === "outlook" ? "bg-blue-100 text-blue-700 hover:bg-blue-200" : 
                                          "bg-indigo-100 text-indigo-700 hover:bg-indigo-200";
                            
                            return (
                              <div
                                key={event.id}
                                className={`text-xs p-1 rounded truncate relative flex items-center ${bgColor}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedEvent(event);
                                  setShowEventDialog(true);
                                }}
                              >
                                <Clock className="w-3 h-3 inline mr-1 flex-shrink-0" />
                                <span className="truncate">{formatTime(event.start_time)} {event.title}</span>
                                {event.provider && event.provider !== "crm" && (
                                  <span className="ml-1 flex-shrink-0" title={`Synced from ${event.provider}`}>
                                    {event.provider === "gmail" ? "📧" : event.provider === "outlook" ? "📅" : ""}
                                  </span>
                                )}
                                {event.meeting_link && (
                                  <span className="ml-1 flex-shrink-0" title="Has meeting link">🔗</span>
                                )}
                              </div>
                            );
                          })}
                          
                          {/* Tasks */}
                          {dayTasks.slice(0, 2).map(task => (
                            <div
                              key={task.id}
                              className={`text-xs p-1 rounded truncate flex items-center space-x-1 ${
                                task.status === "completed" ? "opacity-50 line-through" : ""
                              }`}
                              style={{
                                backgroundColor: task.priority === "high" ? "#fee2e2" : 
                                                 task.priority === "medium" ? "#fef3c7" : "#dcfce7",
                                color: task.priority === "high" ? "#991b1b" : 
                                       task.priority === "medium" ? "#854d0e" : "#166534"
                              }}
                            >
                              <div className={`w-2 h-2 rounded-full ${getPriorityColor(task.priority)}`} />
                              <span className="truncate">{task.title}</span>
                            </div>
                          ))}
                          
                          {/* Show more indicator */}
                          {(dayEvents.length + dayTasks.length) > 4 && (
                            <div className="text-xs text-slate-500 text-center">
                              +{dayEvents.length + dayTasks.length - 4} more
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardHeader>
          <CardTitle>Legend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-indigo-100 border border-indigo-300 rounded" />
              <span className="text-sm text-slate-600">Event/Meeting</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-red-500 rounded" />
              <span className="text-sm text-slate-600">High Priority Task</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-yellow-500 rounded" />
              <span className="text-sm text-slate-600">Medium Priority Task</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-green-500 rounded" />
              <span className="text-sm text-slate-600">Low Priority Task</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Event Dialog */}
      {showEventDialog && (
        <CalendarEventDialog
          open={showEventDialog}
          onClose={() => {
            setShowEventDialog(false);
            setSelectedEvent(null);
            setSelectedDate(null);
          }}
          onSuccess={() => {
            setShowEventDialog(false);
            setSelectedEvent(null);
            setSelectedDate(null);
            fetchCalendarData();
          }}
          event={selectedEvent}
          selectedDate={selectedDate}
        />
      )}
    </div>
  );
};

export default CalendarPage;
